import { useEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Form, FormInstance } from 'antd'
import { omitBy, isNil } from 'lodash'
import moment from 'moment'
import { useRouter } from 'next/router'

import useAnalytics from '@/context/analytics'
import { useLocale } from '@/context/localeContext'
import { useShed } from '@/context/shedContext'
import { UPDATE_USER } from '@/modules/account/graphQL'
import { goTo } from '@/modules/webInduction/goTo'
import { UPDATE_WORKER } from '@/modules/webInduction/graphQL'
import { WEB_INDUCTION_SHED_KEY } from '@/modules/webInduction/webInductionHelper'
import { TRADES } from '@/modules/workers/constants'

import {
  UpdateUserUiMutation,
  UpdateUserUiMutationVariables,
  UpdateWorkerUiMutation,
  UpdateWorkerUiMutationVariables,
  UserWithVisitorUiQuery,
  UserWithWorkerUiQuery,
} from 'types/api.graphql'

export enum Field {
  MOBILE_NUMBER = 'mobileNumber',
  WORKER_TYPE = 'workerType',
  CODE = 'code',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  DOB = 'dob',
  EMAIL = 'email',
  ADDRESS = 'address',
  EMPLOYER = 'subcontractorId',
  TRADE = 'trade',
  TRADE_OTHER = 'tradeOther',
  EMPLOYMENT_STATUS = 'employmentStatus',
  CONTACT_NAME = 'contactName',
  CONTACT_MOBILE = 'contactMobile',
  CONTACT_RELATIONSHIP = 'contactRelationship',
  MEDICAL_HISTORY = 'medicalHistory',
  ALLERGIES = 'allergies',
  TICKETS = 'tickets',
  BUILDER_SWMS = 'builderSwms',
  SUBCONTRACTOR_SWMS = 'subcontractorSwms',
  COMPANY_INDUCTION = 'companyInduction',
  SITE_INDUCTION = 'siteInduction',
  VISIT_REASON = 'visitReason',
  HAS_DONE_PREVIOUS_SITE_INDUCTION = 'hasDonePreviousSiteInduction',
  INDUCTION_TYPE = 'inductionType',
  SIGNATURE_DATA = 'signatureData',
  INDUCTION_IDENTIFIER = 'inductionIdentifier',
}

type UserWithWorker = UserWithWorkerUiQuery['user']
type UserWithVisitor = UserWithVisitorUiQuery['user']

export type BaseTicket =
  UserWithWorkerUiQuery['user']['worker']['tickets'][number]
export type Ticket = Omit<BaseTicket, 'expiry' | 'obtained'> & {
  expiry: moment.Moment
  obtained: moment.Moment
}

export type ReadSwms = {
  createdAt: Date
  swmsDocId: string
  read: boolean
  expired: boolean
  documentSignatureId?: string
  linkToInduction: boolean
}

export type InductionData = {
  responseData: object
  templateId?: string
  libraryTemplateId?: string
}

export type FormFieldValues = {
  [Key in Field]: Key extends Field.TICKETS
    ? Ticket[]
    : Key extends Field.DOB
      ? moment.Moment
      : Key extends Field.SUBCONTRACTOR_SWMS
        ? ReadSwms[]
        : Key extends Field.BUILDER_SWMS
          ? ReadSwms[]
          : Key extends Field.COMPANY_INDUCTION
            ? InductionData
            : Key extends Field.SITE_INDUCTION
              ? InductionData
              : Key extends Field.WORKER_TYPE
                ? 'WORKER' | 'VISITOR'
                : Key extends Field.INDUCTION_TYPE
                  ? 'COMPANY' | 'FULL'
                  : Key extends Field.HAS_DONE_PREVIOUS_SITE_INDUCTION
                    ? boolean
                    : Key extends Field.SIGNATURE_DATA
                      ? []
                      : Key extends Field.INDUCTION_IDENTIFIER
                        ? number
                        : string
}

type WebInductionShedData = {
  userWithWorker?: UserWithWorker
  userWithVisitor?: UserWithVisitor
  formValues?: FormFieldValues
  form: FormInstance
}

type WebInductionData = WebInductionShedData & {
  setFormData: (data: Partial<FormFieldValues>) => void
  updateUser: (data: Partial<FormFieldValues>) => void
  updateWorker: (data: Partial<FormFieldValues>) => void
  ingestUserWithVisitor: (userWithVisitor: UserWithVisitor) => void
  ingestUserWithWorker: (userWithWorker: UserWithWorker) => void
  isLoading: boolean
  goTo: ReturnType<typeof goTo>
  getUserId: () => string
  saveSignature: (signatureData: FormFieldValues['signatureData']) => void
  setInductionIdentifier: (
    inductionIdentifier: FormFieldValues['inductionIdentifier']
  ) => void
}

type CustomShed = {
  shed: WebInductionShedData
  updateShed: (key: string, data: Partial<WebInductionShedData>) => void
  clearShed: () => void
}

export const useWebInduction = (): WebInductionData => {
  const router = useRouter()
  const client = useApolloClient()
  const { tLanguage } = useLocale()
  const { identifyUser } = useAnalytics()
  const { updateShed, shed } = useShed<CustomShed>()

  const [form] = Form.useForm()
  const [isLoading, toggleLoading] = useState(true)

  const shedData = shed[WEB_INDUCTION_SHED_KEY] as WebInductionShedData
  const projectId = router.query.projectId as string

  const setFormData = (formValues: Partial<FormFieldValues>) => {
    updateShed(WEB_INDUCTION_SHED_KEY, {
      ...shedData,
      formValues: {
        ...shedData.formValues,
        ...formValues,
      },
    } as WebInductionShedData)
  }

  const setInductionIdentifier = (inductionIdentifier: number) => {
    setFormData({ inductionIdentifier })
  }

  const updateUser = (data: Partial<FormFieldValues>) => {
    const userVariables: UpdateUserUiMutationVariables['input'] = omitBy(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
      },
      isNil
    )

    return client.mutate<UpdateUserUiMutation, UpdateUserUiMutationVariables>({
      mutation: UPDATE_USER,
      variables: { input: userVariables },
    })
  }

  const updateWorker = (data: Partial<FormFieldValues>) => {
    const otherTrade =
      !TRADES[tLanguage].includes(data.trade) || data.trade === 'Other'

    const workerVariables: UpdateWorkerUiMutationVariables['input'] = omitBy(
      {
        mobile: data.mobileNumber,
        dob: data.dob,
        address: data.address,
        trade: otherTrade ? data.tradeOther : data.trade,
        subcontractorId: data.subcontractorId,
        emergencyContact: data.contactName,
        emergencyContactNumber: data.contactMobile,
        emergencyContactRelationship: data.contactRelationship,
        medicalInformation: data.medicalHistory,
        allergies: data.allergies,
        employmentStatus: data.employmentStatus,
      },
      isNil
    )

    return client.mutate<
      UpdateWorkerUiMutation,
      UpdateWorkerUiMutationVariables
    >({
      mutation: UPDATE_WORKER,
      variables: { input: workerVariables },
    })
  }

  const ingestUserWithVisitor = (userWithVisitor: UserWithVisitor) => {
    const formData: FormFieldValues = {
      workerType: 'VISITOR',
      inductionType: 'FULL',
      code: shedData?.formValues?.code,
      mobileNumber: userWithVisitor?.visitor?.mobile,
      firstName:
        userWithVisitor?.firstName === 'Jar'
          ? null
          : userWithVisitor?.firstName,
      lastName:
        userWithVisitor?.lastName === 'Ron' ? null : userWithVisitor?.lastName,
      email: userWithVisitor?.email,
      visitReason: null,
      address: null,
      allergies: null,
      builderSwms: null,
      companyInduction: null,
      contactMobile: null,
      contactName: null,
      contactRelationship: null,
      dob: null,
      employmentStatus: null,
      hasDonePreviousSiteInduction: null,
      medicalHistory: null,
      siteInduction: null,
      subcontractorId: null,
      trade: null,
      tradeOther: null,
      subcontractorSwms: null,
      tickets: null,
      signatureData: null,
      inductionIdentifier: null,
    }

    updateShed(WEB_INDUCTION_SHED_KEY, {
      ...shedData,
      formValues: { ...shedData.formValues, ...formData },
      userWithVisitor,
    } as WebInductionShedData)

    identifyUser({
      user: {
        id: userWithVisitor.id,
        email: userWithVisitor.email,
        firstName: userWithVisitor.firstName,
        lastName: userWithVisitor.lastName,
        createdAt: userWithVisitor.createdAt,
        visitorId: userWithVisitor?.visitor?.id,
      },
    })
  }

  const ingestUserWithWorker = (userWithWorker: UserWithWorker) => {
    const otherTrade =
      !TRADES[tLanguage].includes(userWithWorker?.worker?.trade) ||
      userWithWorker?.worker?.trade === 'Other'

    const docSignatures = userWithWorker?.worker?.documentSignatures || []

    const ingestSwmsDocs = (docSigns: typeof docSignatures): ReadSwms[] => {
      return docSigns.map((docSign) => ({
        createdAt: new Date(docSign.createdAt),
        read: docSign.expired ? false : true,
        expired: docSign.expired,
        linkToInduction: false,
        swmsDocId: docSign.swmsDoc.id,
        documentSignatureId: docSign.id,
      }))
    }

    const ingestTickets = (tickets: BaseTicket[] | Ticket[]): Ticket[] => {
      return tickets.map((ticket: BaseTicket | Ticket) => ({
        ...ticket,
        expiry: ticket.expiry ? moment(ticket.expiry) : null,
        obtained: ticket.obtained ? moment(ticket.obtained) : null,
      })) as Ticket[]
    }

    const ingestSubcontractors = (
      subbies: UserWithWorker['worker']['subcontractors']
    ): string => {
      if (!subbies) return null

      const projectSubbie = subbies.find((s) =>
        s.projects.some((p) => p.id === projectId)
      )

      return projectSubbie?.id || null
    }

    const formData: FormFieldValues = {
      workerType: 'WORKER',
      inductionType: shedData?.formValues?.inductionType || 'FULL',
      code: shedData?.formValues?.code,
      mobileNumber: userWithWorker?.worker?.mobile,
      firstName:
        userWithWorker?.firstName === 'Jar' ? null : userWithWorker?.firstName,
      lastName:
        userWithWorker?.lastName === 'Ron' ? null : userWithWorker?.lastName,
      dob: userWithWorker?.worker?.dob
        ? moment(userWithWorker?.worker?.dob)
        : null,
      address: userWithWorker?.worker?.address,
      email: userWithWorker?.email,
      trade: otherTrade ? 'Other' : userWithWorker?.worker?.trade,
      tradeOther: otherTrade ? userWithWorker?.worker?.trade : null,
      subcontractorId: ingestSubcontractors(
        userWithWorker?.worker?.subcontractors
      ),
      contactName: userWithWorker?.worker?.emergencyContact,
      contactMobile: userWithWorker?.worker?.emergencyContactNumber,
      contactRelationship: userWithWorker?.worker?.emergencyContactRelationship,
      medicalHistory: userWithWorker?.worker?.medicalInformation,
      allergies: userWithWorker?.worker?.allergies,
      employmentStatus: userWithWorker?.worker?.employmentStatus,
      tickets: ingestTickets(userWithWorker?.worker?.tickets || []),
      builderSwms: ingestSwmsDocs(
        docSignatures.filter((doc) => !!doc.swmsDoc.showAll)
      ),
      subcontractorSwms: ingestSwmsDocs(
        docSignatures.filter((doc) => !doc.swmsDoc.showAll)
      ),
      companyInduction: null,
      siteInduction: null,
      visitReason: null,
      hasDonePreviousSiteInduction: userWithWorker?.worker?.inductions?.some(
        (induction) =>
          induction.__typename === 'ProjectInduction' &&
          induction.project.id === projectId
      ),
      signatureData: null,
      inductionIdentifier: null,
    }

    updateShed(WEB_INDUCTION_SHED_KEY, {
      ...shedData,
      formValues: { ...shedData.formValues, ...formData },
      userWithWorker,
    } as WebInductionShedData)

    identifyUser({
      user: {
        id: userWithWorker.id,
        email: userWithWorker.email,
        firstName: userWithWorker.firstName,
        lastName: userWithWorker.lastName,
        createdAt: userWithWorker.createdAt,
        workerId: userWithWorker?.worker?.id,
      },
    })
  }

  const getUserId = () => {
    return shedData?.userWithWorker?.id || shedData?.userWithVisitor?.id
  }

  const saveSignature = (signatureData: FormFieldValues['signatureData']) => {
    setFormData({ signatureData })
  }

  // on first time this is invoked, save the ant form to the shed, everything will now operate
  // off this form in the shed even when ant generates new forms
  useEffect(() => {
    if (!shedData) {
      updateShed(WEB_INDUCTION_SHED_KEY, { form })
    }
    toggleLoading(false)
  }, [updateShed, shedData, form])

  return {
    setFormData,
    updateWorker,
    updateUser,
    ingestUserWithWorker,
    ingestUserWithVisitor,
    isLoading,
    getUserId,
    saveSignature,
    setInductionIdentifier,
    goTo: goTo(router, projectId, client),
    ...shedData,
  }
}
